import React, { useState, useEffect } from 'react';
import {
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    Tabs,
    Tab,
    Stack,
    Box,
    CircularProgress,
    Grid,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    FormHelperText,
} from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridActionsCellItem,
    GridRowParams,
} from '@mui/x-data-grid';
import {
    DateTimePicker,
} from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CalendarMonth as CalendarIcon,
    Repeat as RepeatIcon,
    Login as ClockInIcon,
    Logout as ClockOutIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ShiftDetailsModal from '../../components/Roster/ShiftDetailsModal';
import RecurringShiftEditModal from '../../components/Roster/RecurringShiftEditModal';
import RecurringShiftDeleteModal from '../../components/Roster/RecurringShiftDeleteModal';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { rosterService, Shift, ShiftSwap, DropdownOption } from '../../services/rosterService';
import { staffService } from '../../services/staffService';
import ShiftFormDialog from '../../components/Roster/ShiftFormDialog';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTimeForApi, formatDateTimeForDisplay, getUserTimezone } from '../../utils/dateUtils';
import { getShiftStatusInfo, getShiftStatusColor, getShiftStatusLabel } from '../../utils/shiftStatusUtils';
import { getStaffDisplayName, getClientDisplayName, enrichShiftsWithNames } from '../../utils/nameUtils';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import PageLayout from '../../components/Layout/PageLayout';
import RoleBasedAccess, { AdminOnly, ManagerOrAbove } from '../../components/Auth/RoleBasedAccess';
import { getUserPrivilegeLevel } from '../../utils/roleUtils';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`roster-tabpanel-${index}`}
            aria-labelledby={`roster-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const RosterPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Helper function to check if user can perform action
    const canPerformAction = (action: string) => {
        const privilegeLevel = getUserPrivilegeLevel(user);

        switch (action) {
            case 'create':
                return privilegeLevel >= 3; // Manager or above
            case 'edit':
                return privilegeLevel >= 3; // Manager or above
            case 'delete':
                return privilegeLevel >= 3; // Manager or above
            case 'view':
                return privilegeLevel >= 1; // All authenticated users
            case 'swap':
                return privilegeLevel >= 1; // All authenticated users can request swaps
            default:
                return false;
        }
    };
    const [tabValue, setTabValue] = useState(0);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [shiftSwaps, setShiftSwaps] = useState<ShiftSwap[]>([]);
    const [clients, setClients] = useState<DropdownOption[]>([]);
    const [staff, setStaff] = useState<DropdownOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [swapsLoading, setSwapsLoading] = useState(true);
    const [dropdownLoading, setDropdownLoading] = useState(false);
    const [openShiftDialog, setOpenShiftDialog] = useState(false);
    const [openSwapDialog, setOpenSwapDialog] = useState(false);
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
    const [selectedSwap, setSelectedSwap] = useState<ShiftSwap | null>(null);
    const [shiftFormData, setShiftFormData] = useState<Partial<Shift>>({});
    const [swapFormData, setSwapFormData] = useState<Partial<ShiftSwap>>({});
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    // Recurring shift edit modal state
    const [recurringEditModalOpen, setRecurringEditModalOpen] = useState(false);
    const [pendingEditShift, setPendingEditShift] = useState<Shift | null>(null);
    
    // Recurring shift delete modal state
    const [recurringDeleteModalOpen, setRecurringDeleteModalOpen] = useState(false);
    const [pendingDeleteShift, setPendingDeleteShift] = useState<{ id: number; shift: any } | null>(null);
    
    // Shift details modal state
    const [shiftDetailsModalOpen, setShiftDetailsModalOpen] = useState(false);
    const [selectedShiftDetails, setSelectedShiftDetails] = useState<Shift | null>(null);
    
    // New state for enhanced functionality
    const [staffAvailability, setStaffAvailability] = useState<{
        isAvailable: boolean;
        conflicts: string[];
        availability: any | null;
    } | null>(null);
    const [availabilityLoading, setAvailabilityLoading] = useState(false);
    const [calculatedDuration, setCalculatedDuration] = useState<number>(0);
    const [selectedStaffProfile, setSelectedStaffProfile] = useState<any>(null);

    useEffect(() => {
        if (user) {
            loadAllData();
        }
    }, [user]);

    const loadAllData = async () => {
        try {
            setLoading(true);
            setDropdownLoading(true);

            // Load dropdown data and shifts in parallel
            const [shiftsData, shiftSwapsData, clientsData, staffData] = await Promise.all([
                rosterService.getShifts(),
                rosterService.getShiftSwaps(),
                rosterService.getClientsDropdown(),
                rosterService.getStaffDropdown()
            ]);

            // Set dropdown data
            setClients(clientsData);
            setStaff(staffData);

            // Enrich shifts with client and staff names using global utility
            const rawShifts = shiftsData.results || shiftsData || [];
            const enrichedShifts = enrichShiftsWithNames(rawShifts as Shift[], staffData, clientsData);

            setShifts(enrichedShifts);
            setShiftSwaps(Array.isArray(shiftSwapsData) ? shiftSwapsData : []);

        } catch (err: any) {
            console.error('Error loading data:', err);
            if (err.response?.status === 401) {
                setError('You are not authorized to view this data. Please log in.');
            } else {
                setError(err.response?.data?.message || 'Failed to load data');
            }
        } finally {
            setLoading(false);
            setSwapsLoading(false);
            setDropdownLoading(false);
        }
    };

    const loadShifts = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await rosterService.getShifts();
            const rawShifts = data.results || data || [];

            // Enrich shifts with client and staff names using global utility
            const enrichedShifts = enrichShiftsWithNames(rawShifts as Shift[], staff, clients);

            setShifts(enrichedShifts);
        } catch (err: any) {
            console.error('Error loading shifts:', err);
            if (err.response?.status === 401) {
                setError('You are not authorized to view shifts. Please log in.');
            } else {
                setError(err.response?.data?.message || 'Failed to load shifts');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadShiftSwaps = async () => {
        try {
            setSwapsLoading(true);
            const data = await rosterService.getShiftSwaps();
            setShiftSwaps(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error('Error loading shift swaps:', err);
        } finally {
            setSwapsLoading(false);
        }
    };

    const loadDropdownData = async () => {
        try {
            setDropdownLoading(true);

            const [clientsData, staffData] = await Promise.all([
                rosterService.getClientsDropdown(),
                rosterService.getStaffDropdown()
            ]);

            setClients(clientsData);
            setStaff(staffData);
        } catch (err: any) {
            console.error('Error loading dropdown data:', err);
            console.error('Error details:', err.response?.data);
            setError(`Failed to load dropdown data: ${err.response?.data?.message || err.message}`);
        } finally {
            setDropdownLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleOpenShiftDialog = (shift?: Shift) => {
        // If editing a recurring shift, show custom modal
        if (shift && (shift.parentShiftId || shift.isRecurrenceParent)) {
            setPendingEditShift(shift);
            setRecurringEditModalOpen(true);
            return;
        }
        
        // For non-recurring shifts, proceed normally
        openShiftDialogWithShift(shift);
    };

    const openShiftDialogWithShift = (shift?: Shift, editChoice?: string) => {
        setSelectedShift(shift || null);

        if (shift) {
            // Store the edit choice for later use in handleSaveShift
            if (editChoice) {
                (shift as any).editChoice = editChoice;
            }

            // When editing, keep the UTC dates as-is from the backend
            // The DateTimePicker components will handle timezone conversion for display
            const formData = {
                ...shift,
                startDateTime: shift.startDateTime || dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                endDateTime: shift.endDateTime || dayjs().add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
            };
            
            // Add UI-specific fields that aren't part of the Shift interface
            (formData as any).creationType = shift.isRecurring || shift.parentShiftId || shift.isRecurrenceParent ? 'recurring' : 'one-off';
            (formData as any).recurrenceType = shift.recurrenceType;
            (formData as any).recurrenceInterval = shift.recurrenceInterval || 1;
            (formData as any).daysOfWeek = shift.daysOfWeek || '';
            (formData as any).dayOfMonth = shift.dayOfMonth;
            (formData as any).recurrenceEndDate = shift.recurrenceEndDate || '';
            (formData as any).maxOccurrences = shift.maxOccurrences || 10;
            (formData as any).endCondition = shift.recurrenceEndDate ? 'endDate' : 'maxOccurrences';
            
            setShiftFormData(formData);
        } else {
            // When creating new, use current local time (not UTC) for better UX
            // The handleDateTimeChange function will convert to UTC when saving
            const now = dayjs();
            setShiftFormData({
                shiftType: 'regular',
                status: 'scheduled',
                hourlyRate: 30,
                clientId: 0, // Use 0 as default number
                staffId: 0, // Use 0 as default number
                startDateTime: now.format('YYYY-MM-DDTHH:mm:ss'),
                endDateTime: now.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
                isRecurring: false,
                creationType: 'one-off',
                recurrenceType: undefined,
                recurrenceInterval: 1,
                daysOfWeek: '',
                dayOfMonth: undefined,
                recurrenceEndDate: '',
                maxOccurrences: 10,
                endCondition: 'endDate'
            } as any);
        }

        setOpenShiftDialog(true);

        // Load dropdown data when dialog opens
        if (clients.length === 0 || staff.length === 0) {
            loadDropdownData();
        }
    };

    const handleRecurringEditChoice = (choice: 'this' | 'future') => {
        if (pendingEditShift) {
            const editChoice = choice === 'this' ? '1' : '2';
            openShiftDialogWithShift(pendingEditShift, editChoice);
        }
        setPendingEditShift(null);
    };

    const handleCloseShiftDialog = () => {
        setOpenShiftDialog(false);
        setSelectedShift(null);
        setShiftFormData({
            shiftType: 'regular',
            status: 'scheduled',
            hourlyRate: 30,
            clientId: 0,
            staffId: 0,
            startDateTime: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss'),
            endDateTime: dayjs().utc().add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
            isRecurring: false,
            creationType: 'one-off',
            recurrenceType: undefined,
            recurrenceInterval: 1,
            daysOfWeek: '',
            dayOfMonth: undefined,
            recurrenceEndDate: '',
            maxOccurrences: 10,
            endCondition: 'endDate'
        } as any);
        setError(null);
        
        // Reset new state
        setStaffAvailability(null);
        setSelectedStaffProfile(null);
        setCalculatedDuration(0);
        setAvailabilityLoading(false);
    };

    const handleSaveShift = async () => {
        try {
            // Validation
            if (!shiftFormData.clientId || shiftFormData.clientId === 0) {
                setError('Please select a client');
                return;
            }

            if (!shiftFormData.staffId || shiftFormData.staffId === 0) {
                setError('Please select a staff member');
                return;
            }

            if (!shiftFormData.startDateTime) {
                setError('Please select a start date and time');
                return;
            }

            if (!shiftFormData.endDateTime) {
                setError('Please select an end date and time');
                return;
            }

            // Hourly rate is no longer entered manually — pay/charge is
            // computed from Billing Rates at payroll/invoice time. Just
            // satisfy the still-required column with 0 below.

            // Check if end time is after start time
            const startTime = dayjs(shiftFormData.startDateTime);
            const endTime = dayjs(shiftFormData.endDateTime);
            if (endTime.isBefore(startTime) || endTime.isSame(startTime)) {
                setError('End time must be after start time');
                return;
            }

            // Check staff availability for non-admin users
            if (staffAvailability && !staffAvailability.isAvailable && !canPerformAction('edit')) {
                setError('Selected staff is not available during the requested time. Please choose a different time or contact an administrator.');
                return;
            }

            const isRecurring = (shiftFormData as any).creationType === 'recurring';
            const shiftData = {
                ...shiftFormData,
                startDateTime: formatDateTimeForApi(shiftFormData.startDateTime),
                endDateTime: formatDateTimeForApi(shiftFormData.endDateTime),
                hourlyRate: 0,
                clientId: Number(shiftFormData.clientId),
                staffId: Number(shiftFormData.staffId),
                isRecurring: isRecurring,
                // Include recurrence fields when creating a recurring shift
                ...(isRecurring && {
                    recurrenceType: (shiftFormData as any).recurrenceType,
                    recurrenceInterval: Number((shiftFormData as any).recurrenceInterval) || 1,
                    daysOfWeek: (shiftFormData as any).daysOfWeek,
                    dayOfMonth: (shiftFormData as any).dayOfMonth ? Number((shiftFormData as any).dayOfMonth) : undefined,
                    recurrenceEndDate: (shiftFormData as any).endCondition === 'endDate' ? formatDateTimeForApi((shiftFormData as any).recurrenceEndDate) : undefined,
                    maxOccurrences: (shiftFormData as any).endCondition === 'maxOccurrences' ? Number((shiftFormData as any).maxOccurrences) : undefined,
                })
            };

            // Debug logging to see what's being sent (only for recurring shifts)
            if (isRecurring) {
                console.log('� Creating RECURRING shift with data:', {
                    isRecurring,
                    recurrenceType: (shiftFormData as any).recurrenceType,
                    recurrenceInterval: (shiftFormData as any).recurrenceInterval,
                    daysOfWeek: (shiftFormData as any).daysOfWeek,
                    maxOccurrences: (shiftFormData as any).maxOccurrences,
                    endCondition: (shiftFormData as any).endCondition,
                    finalPayload: shiftData
                });
            }

            let response;
            if (selectedShift && selectedShift.id) {
                // Check if this is a recurring shift update
                const editChoice = (selectedShift as any).editChoice;
                
                if (editChoice && (selectedShift.parentShiftId || selectedShift.isRecurrenceParent)) {
                    if (editChoice === '1') {
                        // Update only this occurrence
                        response = await rosterService.updateShift(selectedShift.id, shiftData);
                        setSuccess('Single shift occurrence updated successfully!');
                    } else if (editChoice === '2') {
                        // Update this and all future occurrences
                        let parentId = selectedShift.parentShiftId || selectedShift.id;
                        const fromDate = selectedShift.startDateTime.split('T')[0]; // Get date part
                        
                        response = await rosterService.updateRecurringShiftsFrom(parentId!, fromDate, shiftData);
                        setSuccess('This and all future shift occurrences updated successfully!');
                    }
                } else {
                    // Regular single shift update
                    response = await rosterService.updateShift(selectedShift.id, shiftData);
                    setSuccess('Shift updated successfully!');
                }
            } else {
                // Create new shift
                response = await rosterService.createShift(shiftData);
                setSuccess('Shift created successfully!');
            }

            if (response) {
                setError(null);
                setOpenShiftDialog(false);
                loadShifts(); // Reload shifts

                // Reset form with UTC times
                setShiftFormData({
                    shiftType: 'regular',
                    status: 'scheduled',
                    hourlyRate: 30,
                    clientId: 0,
                    staffId: 0,
                    startDateTime: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss'),
                    endDateTime: dayjs().utc().add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
                    isRecurring: false,
                    creationType: 'one-off',
                    recurrenceType: undefined,
                    recurrenceInterval: 1,
                    daysOfWeek: '',
                    dayOfMonth: undefined,
                    recurrenceEndDate: '',
                    maxOccurrences: 10,
                    endCondition: 'endDate'
                } as any);
            }
        } catch (error: any) {
            const operation = selectedShift && selectedShift.id ? 'updating' : 'creating';
            console.error(`Error ${operation} shift:`, error);
            setError(error.response?.data?.message || `Failed to ${operation === 'updating' ? 'update' : 'create'} shift`);
            setSuccess(null);
        }
    }; 
    
    const handleDeleteShift = async (shiftId: number, shift?: any) => {
        // Check if this shift is part of a recurring series
        const shiftToDelete = shift || shifts.find(s => s.id === shiftId);
        
        if (shiftToDelete && (shiftToDelete.parentShiftId || shiftToDelete.isRecurrenceParent)) {
            // This is part of a recurring series - show custom modal
            setPendingDeleteShift({ id: shiftId, shift: shiftToDelete });
            setRecurringDeleteModalOpen(true);
            return;
        }
        
        // Regular one-off shift deletion
        if (window.confirm('Are you sure you want to delete this shift?')) {
            try {
                await rosterService.deleteShift(shiftId);
                setSuccess('Shift deleted successfully');
                loadShifts();
            } catch (err: any) {
                console.error('Error deleting shift:', err);
                setError(err.response?.data?.message || 'Failed to delete shift');
            }
        }
    };

    const handleRecurringDeleteChoice = async (choice: 'this' | 'future') => {
        if (!pendingDeleteShift) return;
        
        const { id: shiftId, shift: shiftToDelete } = pendingDeleteShift;
        
        try {
            if (choice === 'this') {
                // Delete only this occurrence
                await rosterService.deleteShift(shiftId);
                setSuccess('Single shift occurrence deleted successfully');
            } else if (choice === 'future') {
                // Delete this and all future occurrences
                if (shiftToDelete.parentShiftId) {
                    // This is a child shift - delete this and all future children
                    const fromDate = shiftToDelete.startDateTime.split('T')[0]; // Get date part
                    await rosterService.deleteRecurringShiftsFrom(shiftToDelete.parentShiftId, fromDate);
                    setSuccess('This and all future shift occurrences deleted successfully');
                } else if (shiftToDelete.isRecurrenceParent) {
                    // This is the parent - delete the entire recurring pattern
                    const fromDate = shiftToDelete.startDateTime.split('T')[0]; // Get date part
                    await rosterService.deleteRecurringShiftsFrom(shiftId, fromDate);
                    setSuccess('Recurring shift series deleted successfully');
                }
            }
            loadShifts();
        } catch (err: any) {
            console.error('Error deleting recurring shift:', err);
            setError(err.response?.data?.message || 'Failed to delete recurring shift');
        }
        
        setPendingDeleteShift(null);
    };

    const handleUpdateShiftStatus = async (shiftId: number, status: string) => {
        try {
            await rosterService.updateShiftStatus(shiftId, status);
            setSuccess('Shift status updated successfully');
            loadShifts();
        } catch (err: any) {
            console.error('Error updating shift status:', err);
            setError(err.response?.data?.message || 'Failed to update shift status');
        }
    };

    const handleOpenSwapDialog = (swap?: ShiftSwap) => {
        setSelectedSwap(swap || null);
        setSwapFormData(swap || {
            reason: '',
            status: 'pending',
        });
        setOpenSwapDialog(true);
    };

    const handleCloseSwapDialog = () => {
        setOpenSwapDialog(false);
        setSelectedSwap(null);
        setSwapFormData({});
        setError(null);
    };

    const handleOpenShiftDetails = (shift: Shift) => {
        setSelectedShiftDetails(shift);
        setShiftDetailsModalOpen(true);
    };

    const handleCloseShiftDetails = () => {
        setShiftDetailsModalOpen(false);
        setSelectedShiftDetails(null);
    };

    const handleSaveSwap = async () => {
        try {
            // Create the swap data with proper property names
            const swapData = {
                originalShiftId: swapFormData.originalShiftId!,
                requestedToStaffId: swapFormData.requestedToStaffId!,
                reason: swapFormData.reason!
            };
            await rosterService.createShiftSwap(swapData);
            setSuccess('Shift swap request created successfully');
            handleCloseSwapDialog();
            loadShiftSwaps();
        } catch (err: any) {
            console.error('Error creating shift swap:', err);
            setError(err.response?.data?.message || 'Failed to create shift swap');
        }
    };

    const handleApproveSwap = async (swapId: number) => {
        try {
            await rosterService.approveShiftSwap(swapId);
            setSuccess('Shift swap approved successfully');
            loadShiftSwaps();
        } catch (err: any) {
            console.error('Error approving shift swap:', err);
            setError(err.response?.data?.message || 'Failed to approve shift swap');
        }
    };

    const handleClockIn = async (shift: Shift) => {
        try {
            await rosterService.clockIn(shift.id!, shift.staffId);
            setSuccess(`Clocked in for ${shift.staffName || 'staff'} successfully`);
            loadShifts();
        } catch (err: any) {
            console.error('Error clocking in:', err);
            setError(err.response?.data?.message || 'Failed to clock in');
        }
    };

    const handleClockOut = async (shift: Shift) => {
        try {
            await rosterService.clockOut(shift.id!, shift.staffId);
            setSuccess(`Clocked out for ${shift.staffName || 'staff'} successfully`);
            loadShifts();
        } catch (err: any) {
            console.error('Error clocking out:', err);
            setError(err.response?.data?.message || 'Failed to clock out');
        }
    };

    const getStatusChip = (status: string, shift?: any) => {
        // If we have the full shift object, use the unified status utilities
        if (shift) {
            return (
                <Chip
                    label={getShiftStatusLabel(shift)}
                    color={getShiftStatusColor(shift)}
                    size="small"
                />
            );
        }
        
        // Fallback for cases where we only have status string
        const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
            scheduled: 'info',
            in_progress: 'warning',
            completed: 'success',
            cancelled: 'error',
            no_show: 'error',
            pending: 'warning',
            approved: 'success',
            rejected: 'error',
            pending_review: 'warning',
        };
        return (
            <Chip
                label={status.replace('_', ' ').toUpperCase()}
                color={colors[status] || 'default'}
                size="small"
            />
        );
    };

    // Helper function for robust date handling
    const handleDateTimeChange = (field: 'startDateTime' | 'endDateTime', value: any) => {
        try {
            if (value && dayjs.isDayjs(value) && value.isValid()) {
                // Keep the datetime in local format for form state
                // Only convert to UTC when sending to backend in handleSaveShift
                const localDateTime = value.format('YYYY-MM-DDTHH:mm:ss');
                handleShiftInputChange(field, localDateTime);
            } else {
                handleShiftInputChange(field, null);
            }
        } catch (error) {
            console.error(`Error handling ${field} change:`, error);
            handleShiftInputChange(field, null);
        }
    };

    const handleShiftInputChange = (field: string, value: any) => {
        setShiftFormData(prev => {
            const updated = { ...prev, [field]: value };
            
            // When isRecurring changes, also set creationType for consistency
            if (field === 'isRecurring') {
                (updated as any).creationType = value ? 'recurring' : 'one-off';
                
                // Set default recurrence values when enabling recurring
                if (value) {
                    (updated as any).recurrenceType = (updated as any).recurrenceType || 'weekly';
                    (updated as any).recurrenceInterval = (updated as any).recurrenceInterval || 1;
                    (updated as any).daysOfWeek = (updated as any).daysOfWeek || '';
                    (updated as any).maxOccurrences = (updated as any).maxOccurrences || 10;
                    (updated as any).endCondition = (updated as any).endCondition || 'maxOccurrences';
                }
            }
            
            // Auto-calculate duration when start or end time changes
            if (field === 'startDateTime' || field === 'endDateTime') {
                calculateAndUpdateDuration(updated);
            }
            
            // Handle staff selection - fetch hourly rate and reset availability
            if (field === 'staffId' && value) {
                handleStaffSelection(value);
            }
            
            return updated;
        });
    };

    // Calculate duration from start and end times
    const calculateAndUpdateDuration = (formData: Partial<Shift>) => {
        if (formData.startDateTime && formData.endDateTime) {
            const start = dayjs(formData.startDateTime);
            const end = dayjs(formData.endDateTime);
            
            if (end.isAfter(start)) {
                const durationInHours = end.diff(start, 'hour', true);
                setCalculatedDuration(durationInHours);
                
                // Auto-update the durationHours field in form data
                setShiftFormData(prev => ({
                    ...prev,
                    durationHours: durationInHours
                }));
            } else {
                setCalculatedDuration(0);
                setShiftFormData(prev => ({
                    ...prev,
                    durationHours: 0
                }));
            }
        } else {
            setCalculatedDuration(0);
        }
    };

    // Handle staff selection - fetch profile and check availability
    const handleStaffSelection = async (staffId: number) => {
        try {
            // Fetch staff profile to get hourly rate
            const staffProfile = await staffService.getStaffProfile(staffId);
            setSelectedStaffProfile(staffProfile);
            
            // Auto-fill hourly rate from staff profile
            setShiftFormData(prev => ({
                ...prev,
                hourlyRate: staffProfile.hourlyRate || 30 // Default fallback
            }));
            
            // Check availability if we have date/time info
            if (shiftFormData.startDateTime && shiftFormData.endDateTime) {
                await checkStaffAvailability(staffId, shiftFormData.startDateTime, shiftFormData.endDateTime);
            }
        } catch (error) {
            console.error('Error fetching staff profile:', error);
            // Reset to default hourly rate if staff profile fetch fails
            setShiftFormData(prev => ({
                ...prev,
                hourlyRate: 30
            }));
        }
    };

    // Check staff availability for the selected time range
    const checkStaffAvailability = async (staffId: number, startDateTime: string, endDateTime: string) => {
        if (!staffId || !startDateTime || !endDateTime) {
            setStaffAvailability(null);
            return;
        }

        try {
            setAvailabilityLoading(true);
            
            // Try to get staff availability data from backend
            let availability = [];
            try {
                availability = await staffService.getStaffAvailability(staffId);
            } catch (availabilityError) {
                console.warn('Staff availability endpoint not available, using default availability:', availabilityError);
                // Fallback: Create default availability (Monday-Friday 9AM-5PM)
                availability = [
                    { dayOfWeek: 'monday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'tuesday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'wednesday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'thursday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'friday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'saturday', isAvailable: false, startTime: '00:00:00', endTime: '00:00:00' },
                    { dayOfWeek: 'sunday', isAvailable: false, startTime: '00:00:00', endTime: '00:00:00' }
                ];
            }
            
            // Basic availability check based on day of week and time
            const startDate = dayjs(startDateTime);
            const endDate = dayjs(endDateTime);
            const dayOfWeek = startDate.format('dddd').toLowerCase();
            
            const dayAvailability = availability.find(avail => 
                avail.dayOfWeek.toLowerCase() === dayOfWeek && avail.isAvailable
            );
            
            let isAvailable = true;
            let conflicts: string[] = [];
            
            if (!dayAvailability) {
                isAvailable = false;
                conflicts.push(`Staff is not available on ${startDate.format('dddd')}s`);
            } else {
                // Check time ranges
                const shiftStart = startDate.format('HH:mm:ss');
                const shiftEnd = endDate.format('HH:mm:ss');
                
                if (shiftStart < dayAvailability.startTime || shiftEnd > dayAvailability.endTime) {
                    isAvailable = false;
                    conflicts.push(`Shift time (${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}) is outside available hours (${dayAvailability.startTime.slice(0, 5)} - ${dayAvailability.endTime.slice(0, 5)})`);
                }
            }
            
            setStaffAvailability({
                isAvailable,
                conflicts,
                availability: dayAvailability
            });
            
        } catch (error) {
            console.error('Error checking staff availability:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            setStaffAvailability({
                isAvailable: true, // Default to available if check fails
                conflicts: [`Unable to verify availability: ${error.message || 'Unknown error'}`],
                availability: null
            });
        } finally {
            setAvailabilityLoading(false);
        }
    };

    // Update availability check when date/time changes and staff is selected
    useEffect(() => {
        if (shiftFormData.staffId && shiftFormData.startDateTime && shiftFormData.endDateTime) {
            checkStaffAvailability(shiftFormData.staffId, shiftFormData.startDateTime, shiftFormData.endDateTime);
        } else {
            setStaffAvailability(null);
        }
    }, [shiftFormData.staffId, shiftFormData.startDateTime, shiftFormData.endDateTime]);

    const handleSwapInputChange = (field: string, value: any) => {
        setSwapFormData(prev => ({ ...prev, [field]: value }));
    };

    const shiftColumns: GridColDef[] = [
        {
            field: 'clientName',
            headerName: 'Client',
            width: 150,
            valueGetter: (value, row) => row.clientName || `Client ${row.clientId}`,
        },
        {
            field: 'staffName',
            headerName: 'Staff',
            width: 150,
            valueGetter: (value, row) => row.staffName || `Staff ${row.staffId}`,
        },
        {
            field: 'shiftType',
            headerName: 'Type',
            width: 120,
            valueGetter: (value, row) => row.shiftType?.replace('_', ' ').toUpperCase() || 'REGULAR',
        },
        {
            field: 'recurrence',
            headerName: 'Recurrence',
            width: 110,
            renderCell: (params) => {
                const row = params.row;
                if (row.isRecurrenceParent) {
                    return (
                        <Chip
                            label="Parent"
                            color="primary"
                            size="small"
                            icon={<RepeatIcon />}
                        />
                    );
                } else if (row.parentShiftId) {
                    return (
                        <Chip
                            label="Series"
                            color="secondary"
                            size="small"
                            variant="outlined"
                        />
                    );
                } else {
                    return (
                        <Chip
                            label="One-off"
                            color="default"
                            size="small"
                            variant="outlined"
                        />
                    );
                }
            },
        },
        {
            field: 'startDateTime',
            headerName: 'Start Time',
            width: 180,
            valueGetter: (value, row) => formatDateTimeForDisplay(row.startDateTime, 'MMM DD, YYYY HH:mm'),
        },
        {
            field: 'endDateTime',
            headerName: 'End Time',
            width: 180,
            valueGetter: (value, row) => formatDateTimeForDisplay(row.endDateTime, 'MMM DD, YYYY HH:mm'),
        },
        {
            field: 'durationHours',
            headerName: 'Duration',
            width: 100,
            valueGetter: (value, row) => `${row.durationHours || 0}h`,
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            renderCell: (params) => getStatusChip(params.value, params.row),
        },
        {
            field: 'totalAmount',
            headerName: 'Amount',
            width: 100,
            valueGetter: (value, row) => `$${row.totalAmount || 0}`,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 200,
            getActions: (params: GridRowParams) => {
                const actions = [];
                const shift: Shift = params.row;
                const canClock = canPerformAction('edit');

                // Clock In: show when shift is scheduled and not yet started
                if (canClock && !shift.actualStartTime && ['scheduled', 'in_progress'].includes(shift.status)) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<ClockInIcon />}
                            label="Clock In"
                            onClick={() => handleClockIn(shift)}
                        />
                    );
                }

                // Clock Out: show when shift has been clocked in but not yet out
                if (canClock && shift.actualStartTime && !shift.actualEndTime) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<ClockOutIcon />}
                            label="Clock Out"
                            onClick={() => handleClockOut(shift)}
                        />
                    );
                }

                if (canPerformAction('edit')) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<EditIcon />}
                            label="Edit"
                            onClick={() => handleOpenShiftDialog(params.row)}
                            showInMenu
                        />
                    );
                }

                if (canPerformAction('delete')) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={() => handleDeleteShift(params.id as number, params.row)}
                            showInMenu
                        />
                    );
                }

                return actions;
            },
        },
    ];

    const swapColumns: GridColDef[] = [
        {
            field: 'originalShiftId',
            headerName: 'Shift ID',
            width: 100,
        },
        {
            field: 'requestingStaffId',
            headerName: 'Requesting Staff',
            width: 150,
            valueGetter: (value, row) => row.requesterName || `Staff ${row.requestingStaffId}`,
        },
        {
            field: 'targetStaffId',
            headerName: 'Target Staff',
            width: 150,
            valueGetter: (value, row) => row.targetStaffId ? (row.responderName || `Staff ${row.targetStaffId}`) : 'Any Staff',
        },
        {
            field: 'reason',
            headerName: 'Reason',
            width: 200,
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            renderCell: (params) => getStatusChip(params.value, params.row),
        },
        {
            field: 'createdAt',
            headerName: 'Requested',
            width: 180,
            valueGetter: (value, row) => formatDateTimeForDisplay(row.createdAt || row.requestedAt, 'MMM DD, HH:mm'),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            getActions: (params: GridRowParams) => {
                const actions = [];

                // Only managers and above can approve swap requests
                if (params.row.status === 'pending' && canPerformAction('edit')) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<EditIcon />}
                            label="Approve"
                            onClick={() => handleApproveSwap(params.id as number)}
                        />
                    );
                }

                return actions;
            },
        },
    ];

    if (!user) {
        return (
            <PageLayout>
                <Alert severity="warning">
                    Please log in to view roster.
                </Alert>
            </PageLayout>
        );
    }

    return (
        <LocalizationProvider 
            dateAdapter={AdapterDayjs}
            adapterLocale="en"
        >
            <PageLayout>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Roster Management
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<CalendarIcon />}
                        onClick={() => navigate('/roster/calendar')}
                    >
                        Calendar View
                    </Button>
                </Box>

                <Paper sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange}>
                            <Tab label="Shifts" />
                            <Tab label="Shift Swaps" />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Scheduled Shifts</Typography>
                            <ManagerOrAbove>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenShiftDialog()}
                                >
                                    Add Shift
                                </Button>
                            </ManagerOrAbove>
                        </Box>
                        <Box sx={{ height: 500, width: '100%' }}>
                            <DataGrid
                                rows={shifts}
                                columns={shiftColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 10 },
                                    },
                                }}
                                pageSizeOptions={[5, 10, 25]}
                                loading={loading}
                                disableRowSelectionOnClick
                                onRowClick={(params) => handleOpenShiftDetails(params.row)}
                                sx={{
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }
                                }}
                                slotProps={{
                                    noRowsOverlay: {
                                        children: shifts.length === 0 && !loading ? (
                                            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    No shifts scheduled
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Create your first shift to get started
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleOpenShiftDialog()}
                                                >
                                                    Add Shift
                                                </Button>
                                            </Stack>
                                        ) : null
                                    }
                                }}
                            />
                        </Box>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Shift Swap Requests</Typography>
                            <RoleBasedAccess requiredPrivilegeLevel={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenSwapDialog()}
                                >
                                    Request Swap
                                </Button>
                            </RoleBasedAccess>
                        </Box>
                        <Box sx={{ height: 500, width: '100%' }}>
                            <DataGrid
                                rows={shiftSwaps}
                                columns={swapColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 10 },
                                    },
                                }}
                                pageSizeOptions={[5, 10, 25]}
                                loading={swapsLoading}
                                disableRowSelectionOnClick
                                slotProps={{
                                    noRowsOverlay: {
                                        children: shiftSwaps.length === 0 && !swapsLoading ? (
                                            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    No shift swap requests
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Create your first swap request to get started
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleOpenSwapDialog()}
                                                >
                                                    Request Swap
                                                </Button>
                                            </Stack>
                                        ) : null
                                    }
                                }}
                            />
                        </Box>
                    </TabPanel>
                </Paper>

                {/* Add/Edit Shift Dialog (shared component) */}
                <ShiftFormDialog
                    open={openShiftDialog}
                    onClose={handleCloseShiftDialog}
                    shift={selectedShift}
                    clients={clients}
                    staff={staff}
                    canEditShifts={canPerformAction('edit')}
                    onSaved={() => {
                        setSuccess(selectedShift ? 'Shift updated successfully!' : 'Shift created successfully!');
                        setError(null);
                        loadShifts();
                    }}
                />

                {/* Add Shift Swap Dialog */}
                <Dialog open={openSwapDialog} onClose={handleCloseSwapDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Request Shift Swap</DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <FormControl fullWidth required>
                                <InputLabel>Original Shift</InputLabel>
                                <Select
                                    value={swapFormData.originalShiftId || ''}
                                    onChange={(e) => handleSwapInputChange('originalShiftId', e.target.value)}
                                >
                                    {shifts.map((shift) => (
                                        <MenuItem key={shift.id} value={shift.id}>
                                            Shift #{shift.id} - {shift.clientName}
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                {new Date(shift.startDateTime).toLocaleString()}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth required>
                                <InputLabel>Requesting Staff</InputLabel>
                                <Select
                                    value={swapFormData.requestedByStaffId || ''}
                                    onChange={(e) => handleSwapInputChange('requestedByStaffId', e.target.value)}
                                >
                                    {staff.filter(s => s.name).map((staffMember) => (
                                        <MenuItem key={staffMember.id} value={staffMember.id}>
                                            {getStaffDisplayName(staffMember)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Target Staff (Optional)</InputLabel>
                                <Select
                                    value={swapFormData.requestedToStaffId || ''}
                                    onChange={(e) => handleSwapInputChange('requestedToStaffId', e.target.value || undefined)}
                                >
                                    <MenuItem value="">Any Staff</MenuItem>
                                    {staff.filter(s => s.name).map((staffMember) => (
                                        <MenuItem key={staffMember.id} value={staffMember.id}>
                                            {getStaffDisplayName(staffMember)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Reason for Swap"
                                fullWidth
                                required
                                multiline
                                rows={3}
                                value={swapFormData.reason || ''}
                                onChange={(e) => handleSwapInputChange('reason', e.target.value)}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseSwapDialog}>Cancel</Button>
                        <Button onClick={handleSaveSwap} variant="contained">
                            Request Swap
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Recurring Shift Edit Modal */}
                <RecurringShiftEditModal
                    open={recurringEditModalOpen}
                    onClose={() => {
                        setRecurringEditModalOpen(false);
                        setPendingEditShift(null);
                    }}
                    onConfirm={handleRecurringEditChoice}
                    shiftInfo={{
                        title: pendingEditShift ? `${pendingEditShift.shiftType} Shift` : undefined,
                        startDateTime: pendingEditShift?.startDateTime,
                        isRecurrenceParent: pendingEditShift?.isRecurrenceParent,
                        hasChildren: pendingEditShift?.isRecurrenceParent
                    }}
                />

                {/* Recurring Shift Delete Modal */}
                <RecurringShiftDeleteModal
                    open={recurringDeleteModalOpen}
                    onClose={() => {
                        setRecurringDeleteModalOpen(false);
                        setPendingDeleteShift(null);
                    }}
                    onConfirm={handleRecurringDeleteChoice}
                    shiftInfo={{
                        title: pendingDeleteShift ? `${pendingDeleteShift.shift.shiftType} Shift` : undefined,
                        startDateTime: pendingDeleteShift?.shift.startDateTime,
                        isRecurrenceParent: pendingDeleteShift?.shift.isRecurrenceParent,
                        hasChildren: pendingDeleteShift?.shift.isRecurrenceParent
                    }}
                />

                {/* Shift Details Modal */}
                <ShiftDetailsModal
                    open={shiftDetailsModalOpen}
                    onClose={handleCloseShiftDetails}
                    shift={selectedShiftDetails}
                />
            </PageLayout>
        </LocalizationProvider>
    );
};

export default RosterPage;
